import { trackAnalytics } from './analyticsService';

/**
 * Typed catalog of finder PostHog events, mirroring the product spec
 * ("PostHog Product Analytics Events - Finder Events").
 *
 * Property bags are forwarded verbatim to the BE `properties`. The super
 * properties `organization_id` / `salesforce_organization_id` are injected
 * server-side by SendAnalytics — do NOT set them here. `product` is also a
 * server-set super property, but we additionally stamp it on every event's
 * data (see `trackFinderEvent`) so it lands as a per-event property too.
 *
 * Wire names are snake_case on purpose: they are the PostHog contract, not
 * internal app state, so they stay as the analytics team defined them.
 */
/** Per-event product tag, stamped on every Finder event's data bag. */
export const FINDER_PRODUCT = 'finder';

export const FinderEvent = {
  SearchSubmitted: 'search_submitted',
  SearchResultsViewed: 'search_results_viewed',
  SchoolProfileViewed: 'school_profile_viewed',
  SchoolFavorited: 'school_favorited',
  CompareViewed: 'compare_viewed',
  FiltersOpened: 'filters_opened',
  FiltersApplied: 'filters_applied',
  ApplicationCtaClicked: 'application_cta_clicked',
  BrowserLanguageNotSupported: 'browser_language_not_supported',
} as const;

export type FinderEventName = typeof FinderEvent[keyof typeof FinderEvent];

/** IDs may arrive as the BE numeric id or a slug-like string. */
export type EntityId = string | number;

/** On every event. `search_session_id` is provisional in the spec (marked ???). */
interface BaseProps {
  language: string;
  search_session_id?: string;
}

/** zip is a string: 5 digits and may start with a leading 0 (e.g. "02139"). */
interface LocationProps {
  city?: string;
  state?: string;
  zip?: string;
  grade?: string;
}

interface BoundaryProps {
  in_district?: boolean;
  boundary_id?: number;
}

/** Flattened filter keys: distance in miles + dynamic `filter_<section_key>`. */
export interface FilterValueProps {
  filter_max_distance?: number;
  filter_seat_availability?: string[];
  [key: `filter_${string}`]: number | string[] | undefined;
}

export interface SearchSubmittedProps extends BaseProps, LocationProps {
  entered_from: 'landing' | 'search_results' | 'recent_search';
  search_type: 'address_and_grade' | 'specific_school';
  dark_mode: boolean;
}

export interface SearchResultsViewedProps
  extends BaseProps, LocationProps, BoundaryProps, FilterValueProps {
  entered_from?: 'landing' | 'search_results' | 'recent_search';
  schools_found: number;
  zero_results: boolean;
  sort_order?: 'distance' | 'name' | 'enrollment';
  results_view_type: 'map' | 'list';
  filters_applied: boolean;
  top_3_visible_school_ids: EntityId[];
  dark_mode: boolean;
}

export interface SchoolProfileViewedProps
  extends BaseProps, LocationProps, BoundaryProps, FilterValueProps {
  entered_from?: 'specific_school_search' | 'search_results' | 'direct_url' | 'compare_modal';
  school_id: EntityId;
  distance?: number;
  schools_found?: number;
  filters_applied?: boolean;
  search_result_rank?: number;
  dark_mode: boolean;
}

/** Same search-context shape as `school_profile_viewed`; where = the surface. */
export interface SchoolFavoritedProps
  extends BaseProps, LocationProps, BoundaryProps, FilterValueProps {
  entered_from?: 'search_results' | 'map_popup' | 'school_profile';
  school_id: EntityId;
  distance?: number;
  schools_found?: number;
  filters_applied?: boolean;
  search_result_rank?: number;
  dark_mode: boolean;
}

export interface CompareViewedProps extends BaseProps {
  entered_from?: 'search_results' | 'compare_school_dropdown';
  schools_compared_ordered: EntityId[];
  comparison_count: number;
}

export type FiltersOpenedProps = BaseProps;

export interface FiltersAppliedProps
  extends BaseProps, LocationProps, BoundaryProps, FilterValueProps {}

export interface ApplicationCtaClickedProps extends BaseProps {
  school_id: EntityId;
}

/** Visitor's browser language is unsupported by the org; we fell back to English. */
export interface BrowserLanguageNotSupportedProps extends BaseProps {
  browser_language: string;
  browser_base_language: string;
  org_enabled_languages: string[];
}

/** Maps each event to its required property shape for compile-time enforcement. */
export interface FinderEventProps {
  [FinderEvent.SearchSubmitted]: SearchSubmittedProps;
  [FinderEvent.SearchResultsViewed]: SearchResultsViewedProps;
  [FinderEvent.SchoolProfileViewed]: SchoolProfileViewedProps;
  [FinderEvent.SchoolFavorited]: SchoolFavoritedProps;
  [FinderEvent.CompareViewed]: CompareViewedProps;
  [FinderEvent.FiltersOpened]: FiltersOpenedProps;
  [FinderEvent.FiltersApplied]: FiltersAppliedProps;
  [FinderEvent.ApplicationCtaClicked]: ApplicationCtaClickedProps;
  [FinderEvent.BrowserLanguageNotSupported]: BrowserLanguageNotSupportedProps;
}

/**
 * Type-safe finder event tracker. The property bag is checked against the
 * event's schema at the call site, then forwarded fire-and-forget.
 */
export function trackFinderEvent<E extends FinderEventName>(
  event: E,
  props: FinderEventProps[E],
): Promise<void> {
  return trackAnalytics(event, {
    ...(props as unknown as Record<string, unknown>),
    product: FINDER_PRODUCT,
  });
}
